/**
 * TEXT SCRAMBLE + TYPEWRITER EFFECT
 * ================================
 *
 * A progressive, scroll-triggered text animation system with two styles:
 *
 * 1) "decrypt" (default)
 *    - Hacker / terminal / Matrix-style decryption effect
 *    - Each character cycles through random glyphs before settling
 *
 * 2) "type"
 *    - Old-school console typewriter effect
 *    - Characters reveal left-to-right with a caret cursor
 *    - Supports block (▮) and underscore (_) cursors
 *
 *
 * CORE FEATURES
 * -------------
 * - Word-safe wrapping: text never breaks mid-word
 * - Pre-measured character widths to prevent layout shifting
 * - Fully responsive: browser handles line wrapping naturally
 * - Supports real paragraphs via newline parsing
 * - Scroll-based triggering using IntersectionObserver
 *
 *
 * SCOPED SEQUENTIAL ANIMATION (IMPORTANT)
 * --------------------------------------
 * Scramble animations are scoped by container, not global.
 *
 * - Elements are grouped by their nearest parent with:
 *     data-scramble-group
 *
 * - Within a group:
 *     Elements animate sequentially (first → second → third)
 *
 * - Between groups:
 *     Groups animate independently when they enter the viewport
 *
 * This means:
 * - Hero text can type line-by-line in order
 * - Footer or deep-linked sections will animate immediately when reached
 * - Users never see empty content because another section is still animating
 *
 * Example:
 *
 *   <section data-scramble-group="hero">
 *     <p class="scramble-text">Line one</p>
 *     <p class="scramble-text">Line two</p>
 *   </section>
 *
 *   <footer data-scramble-group="footer">
 *     <p class="scramble-text">Contact us</p>
 *   </footer>
 *
 *
 * BASIC USAGE
 * ----------
 * 1) Add class "scramble-text" to any element you want to animate.
 *
 * 2) Provide text in one of two ways:
 *
 *    A) SEO-friendly (recommended):
 *       Text exists in HTML and is visible without JavaScript.
 *
 *       <p class="scramble-text">Hello World</p>
 *
 *    B) Decorative / JS-only:
 *       Text provided via data attribute.
 *
 *       <p class="scramble-text" data-scramble="Hello World"></p>
 *
 * 3) Animation triggers automatically when the group enters the viewport.
 *
 *
 * PARAGRAPHS + NEWLINES
 * --------------------
 * - Single newline (\n) becomes a normal space
 *   → keeps responsive wrapping intact
 *
 * - Two or more newlines (\n\n) create separate paragraphs
 *
 * Example:
 *   data-scramble="Line one.\n\nLine two."
 *
 *
 * STYLE MODES
 * ----------
 * data-scramble-style="decrypt"   (default)
 * data-scramble-style="type"
 *
 * Examples:
 *   <p class="scramble-text">Decrypt effect</p>
 *   <p class="scramble-text" data-scramble-style="type">Typewriter effect</p>
 *
 *
 * SPEED CONTROLS (optional)
 * ------------------------
 * data-scramble-speed="20"
 *   - Frame delay in milliseconds
 *   - Higher = slower, lower = faster
 *   - Defaults:
 *       decrypt: 15
 *       type:    25
 *
 * data-scramble-step="2"
 *   - Controls how sequential the reveal is
 *   - Higher = wave-like / staggered
 *   - Lower = more simultaneous
 *   - Defaults:
 *       decrypt: 1.5
 *       type:    1
 *
 * data-scramble-duration="10"   (decrypt only)
 *   - Base scramble duration per character
 *   - Default: 5
 *
 *
 * CARET CONTROLS (typewriter mode)
 * -------------------------------
 * By default:
 * - Caret is enabled in "type" mode
 * - Caret style = block (▮)
 * - Caret blinks during typing
 * - Caret hides when typing finishes
 *
 * data-scramble-caret-style="block" | "underscore" | "none"
 *
 * data-scramble-caret="▮" or "_"
 *   - Overrides the caret glyph entirely
 *
 * data-scramble-caret-blink="true|false"
 *   - Controls blinking during typing (default: true)
 *
 * data-scramble-caret-end="hide" | "stay" | "stay-solid"
 *   - hide       → caret disappears at end (default)
 *   - stay       → caret remains blinking
 *   - stay-solid → caret remains but stops blinking
 *
 *
 * CARET PRE-START (TYPE MODE)
 * --------------------------
 * Optional “pre-roll” state before typing begins.
 *
 * data-scramble-caret-start="none"  (default)
 * data-scramble-caret-start="blink"
 * data-scramble-caret-start="pause"
 *
 * data-scramble-caret-start-delay="700"
 *   - Duration of pre-start phase in milliseconds
 *
 * - "blink": caret blinks once at the start, then typing begins
 * - "pause": caret is visible and idle, then typing begins
 *
 *
 * OPTIONAL TERMINAL PROMPT
 * -----------------------
 * Adds a static prompt before the text (opt-in).
 *
 * data-scramble-prompt=">"
 * data-scramble-prompt-spacing=" "
 *
 * Example:
 *   <p class="scramble-text"
 *      data-scramble-style="type"
 *      data-scramble-prompt=">">
 *     npm run dev
 *   </p>
 *
 *
 * CUSTOMIZATION NOTES
 * ------------------
 * - Character set for decrypt mode: modify `this.chars`
 * - Caret positioning and appearance: see text-scramble.css
 * - Grouping behavior: controlled via data-scramble-group
 *
 *
 * PERFORMANCE + ACCESSIBILITY
 * --------------------------
 * - Uses requestAnimationFrame for smooth animation
 * - Pre-creates spans (no innerHTML rebuilds per frame)
 * - Each element animates once per page load
 * - Progressive enhancement: text can live in HTML for SEO and screen readers
 */

class TextScramble {
  constructor(element) {
    this.element = element;
    this.disableWidthLock = /^H[1-6]$/.test(this.element.tagName);
    // ...style="type", default decrypt
    this.style = element.getAttribute("data-scramble-style") || "decrypt";
    // caret style: "block" | "underscore" | "none"
    this.caretStyle = element.getAttribute("data-scramble-caret-style") || (this.style === "type" ? "block" : "none"); // default: typing shows caret, decrypt doesn't
    this.caretBlink = element.getAttribute("data-scramble-caret-blink") !== "false";

    // caret pre-start behavior
    // "none" | "blink" | "pause"
    this.caretStart = element.getAttribute("data-scramble-caret-start") || "none";

    // Optional terminal prompt shown before the text/caret (default: off)
    this.prompt = element.getAttribute("data-scramble-prompt") || ""; // e.g. ">" or "$"
    this.promptSpacing = element.getAttribute("data-scramble-prompt-spacing") || " "; // default " "
    this.promptEl = null;

    // duration in ms (default ~1 blink)
    this.caretStartDelay = parseInt(element.getAttribute("data-scramble-caret-start-delay")) || 700;

    // allow overriding the actual glyph if you want
    this.caretChar = element.getAttribute("data-scramble-caret") || null;
    // "hide" | "stay" | "stay-solid"
    this.caretEnd = element.getAttribute("data-scramble-caret-end") || "hide";
    this.caret = null;
    // Characters to cycle through during scramble effect
    this.chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*";

    // SPEED CONTROLS: Read from data attributes or use defaults
    this.speed = parseInt(element.getAttribute("data-scramble-speed")) || (this.style === "type" ? 25 : 15);
    this.step = parseFloat(element.getAttribute("data-scramble-step")) || (this.style === "type" ? 1 : 1.5);
    this.duration = parseInt(element.getAttribute("data-scramble-duration")) || 5;

    // HYBRID APPROACH: Use data-scramble attribute OR existing DOM content
    // Priority: data-scramble (plain text) > existing HTML (rich text) > textContent (fallback)
    this.dataScramble = element.getAttribute("data-scramble");

    // If the element contains markup (e.g. <span class="highlight">FUN</span>),
    // store the original HTML so we can rebuild it while preserving inline styles.
    this.sourceHTML = null;
    if (!this.dataScramble) {
      const hasMarkup = element.querySelector("*"); // any child element means styled HTML
      if (hasMarkup) this.sourceHTML = element.innerHTML;
    }

    // This is the final string used for timing/queue (safe even when HTML exists)
    this.originalText = this.dataScramble || element.textContent.trim();

    // If using SEO-friendly HTML/text, clear it for animation AFTER capturing sourceHTML
    if (!this.dataScramble) {
      element.textContent = "";
    }

    this.hasStarted = false;
    this.frameRequest = null;
    this.frame = 0;
    this.queue = [];
    this.resolve = null;
    this.spans = []; // Store references to character spans
  }

  unlock() {
    // Remove fixed widths so text becomes responsive again.
    // Keep it safe: only touch spans we created.
    if (!this.spans || this.spans.length === 0) return;

    for (const span of this.spans) {
      if (!span) continue;
      span.style.width = "";
      span.style.minWidth = "";
      span.style.display = "";
      // optional: if you set any per-char alignment styles
      // span.style.textAlign = "";
    }

    // If you also want word wrappers to stop affecting layout:
    // (usually fine to leave them)
    // const words = this.element.querySelectorAll(".scramble-word");
    // words.forEach(w => w.style.whiteSpace = "");

    this._unlocked = true;
  }

  finalize() {
    const keepCaret = this.caret && this.caretEnd !== "hide";
    const savedCaret = keepCaret ? this.caret : null;

    // only remove caret if it's not meant to stay
    if (!keepCaret && this.caret?.parentNode) {
      this.caret.parentNode.removeChild(this.caret);
      this.caret = null;
    }

    if (this.promptEl?.parentNode) this.promptEl.parentNode.removeChild(this.promptEl);
    this.promptEl = null;

    // restore original content
    if (this.sourceHTML && !this.dataScramble) {
      this.element.innerHTML = this.sourceHTML;
    } else {
      this.element.textContent = this.originalText;
    }

    // re-append caret after content restore
    if (savedCaret) {
      const last = this.element.lastChild;
      if (last && last.nodeType === Node.TEXT_NODE) {
        last.textContent = last.textContent.trimEnd();
      }
      this.element.appendChild(savedCaret);
    }

    this.element.classList.remove("is-scrambling");
  }

  moveCaretToIndex(index) {
    if (!this.caret) return;

    // If nothing revealed yet, keep caret at the start of the container
    if (index < 0) {
      const parent = this.caret.parentNode || this.element;

      // If there's a prompt, caret should start AFTER it: ">_"
      if (this.promptEl && this.promptEl.parentNode === parent) {
        parent.insertBefore(this.caret, this.promptEl.nextSibling);
      } else {
        parent.insertBefore(this.caret, parent.firstChild);
      }

      return;
    }

    const lastSpan = this.spans[index];
    if (!lastSpan) return;

    // Ensure caret is in same container as the span (important with paragraphs)
    const parent = lastSpan.parentNode;
    parent.appendChild(this.caret); // default
    parent.insertBefore(this.caret, lastSpan.nextSibling); // place right after span
  }

  /**
   * Process a plain text string into word-safe wrappers + per-character spans
   * (uses the same logic as the old createSpans path).
   */
  _processTextToSpans(text, container, temp, state) {
    if (!text) return;

    // Normalize newlines: keep responsive wrapping
    text = text.replace(/\r\n/g, "\n").replace(/\n/g, " ");

    const tokens = text.match(/([^\s]+|\s+)/g) || [];
    for (const tok of tokens) {
      if (/^\s+$/.test(tok)) {
        container.appendChild(document.createTextNode(tok));
        continue;
      }

      const wordWrap = document.createElement("span");
      wordWrap.className = "scramble-word";

      for (let i = 0; i < tok.length; i++) {
        const finalChar = tok[i];

        // measure width of final char (prevents layout jump)
        const measureSpan = document.createElement("span");
        measureSpan.style.display = "inline-block";
        measureSpan.textContent = finalChar;
        temp.appendChild(measureSpan);
        const w = measureSpan.offsetWidth;
        temp.removeChild(measureSpan);

        const span = document.createElement("span");
        span.className = "scramble-char";
        span.style.display = "inline-block";
        if (!this.disableWidthLock) {
          span.style.width = w + "px";
          span.style.minWidth = w + "px";
        }
        span.style.opacity = "0";
        span.textContent = "\u00A0";

        wordWrap.appendChild(span);

        const start = Math.floor(state.animIndex * this.step);
        const end = start + Math.floor(Math.random() * 8) + this.duration;
        this.queue.push({ to: finalChar, start, end, char: null });

        this.spans.push(span);
        state.animIndex++;
      }

      container.appendChild(wordWrap);

      // track last container we wrote into (for caret append)
      state.lastContainer = container;
    }
  }

  /**
   * Walk a DOM subtree and rebuild it into `outParent`,
   * preserving element nodes (e.g. <span class="highlight">) while
   * inserting per-character spans inside text nodes.
   */
  _processNodesToSpans(nodes, outParent, temp, state) {
    for (const node of nodes) {
      // Text node
      if (node.nodeType === Node.TEXT_NODE) {
        this._processTextToSpans(node.textContent, outParent, temp, state);
        continue;
      }

      // Element node
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();

        // Preserve <br> if present (optional)
        if (tag === "br") {
          outParent.appendChild(document.createElement("br"));
          state.lastContainer = outParent;
          continue;
        }

        // Clone element WITHOUT children, keep attributes/classes/styles
        const clone = node.cloneNode(false);
        outParent.appendChild(clone);

        // Recurse into children
        this._processNodesToSpans(Array.from(node.childNodes), clone, temp, state);
      }
    }
  }

  /**
   * Pre-create span elements for each character to prevent layout jumping
   * Each span is measured with its final character and locked to that width
   * @param {string} text - The final text to display
   */
  createSpans(text) {
    this.element.innerHTML = "";
    this.spans = [];
    this.queue = [];

    text = text.replace(/\r\n/g, "\n");

    const computedStyle = window.getComputedStyle(this.element);

    const temp = document.createElement("div");
    temp.style.position = "absolute";
    temp.style.visibility = "hidden";
    temp.style.whiteSpace = "nowrap";
    temp.style.left = "-9999px";
    temp.style.font = computedStyle.font;
    temp.style.letterSpacing = computedStyle.letterSpacing;
    document.body.appendChild(temp);

    // State shared between builders
    const state = { animIndex: 0, lastContainer: this.element };

    // RICH HTML PATH: Preserve inline markup (e.g. <span class="highlight">FUN</span>)
    if (this.sourceHTML && !this.dataScramble) {
      const tpl = document.createElement("template");
      tpl.innerHTML = this.sourceHTML;

      // Rebuild the element from its original DOM structure
      this._processNodesToSpans(Array.from(tpl.content.childNodes), this.element, temp, state);
    } else {
      const paragraphs = text.trim().split(/\n{2,}/);

      paragraphs.forEach((para, pIndex) => {
        // single newlines inside a paragraph become spaces (responsive)
        const paraText = para.replace(/\n/g, " ");

        // create a paragraph wrapper only if the original had multiple paragraphs
        const container = paragraphs.length > 1 ? document.createElement("p") : this.element;
        state.lastContainer = container;

        if (container !== this.element) {
          container.className = "scramble-paragraph";
          this.element.appendChild(container);
        }

        const tokens = paraText.match(/([^\s]+|\s+)/g) || [];

        for (const tok of tokens) {
          if (/^\s+$/.test(tok)) {
            container.appendChild(document.createTextNode(tok));
            continue;
          }

          const wordWrap = document.createElement("span");
          wordWrap.className = "scramble-word";

          for (let i = 0; i < tok.length; i++) {
            const finalChar = tok[i];

            const measureSpan = document.createElement("span");
            measureSpan.style.display = "inline-block";
            measureSpan.textContent = finalChar;
            temp.appendChild(measureSpan);
            const w = measureSpan.offsetWidth;
            temp.removeChild(measureSpan);

            const span = document.createElement("span");
            span.className = "scramble-char";
            span.style.display = "inline-block";
            if (!this.disableWidthLock) {
              span.style.width = w + "px";
              span.style.minWidth = w + "px";
            }
            span.style.opacity = "0";
            span.textContent = "\u00A0";

            wordWrap.appendChild(span);

            const start = Math.floor(state.animIndex * this.step);
            const end = start + Math.floor(Math.random() * 8) + this.duration;
            this.queue.push({ to: finalChar, start, end, char: null });

            this.spans.push(span);
            state.animIndex++;
          }

          container.appendChild(wordWrap);
        }
      });
    }

    // Remove previous prompt if any
    if (this.promptEl && this.promptEl.parentNode) {
      this.promptEl.parentNode.removeChild(this.promptEl);
    }
    this.promptEl = null;

    // Add prompt (opt-in)
    if (this.prompt && this.style === "type") {
      const firstContainer = this.element.querySelector(".scramble-paragraph") || this.element;

      const promptSpan = document.createElement("span");
      promptSpan.className = "scramble-prompt";
      promptSpan.textContent = this.prompt + (this.promptSpacing ?? " ");

      // Insert prompt at the very start of the text
      firstContainer.insertBefore(promptSpan, firstContainer.firstChild);

      this.promptEl = promptSpan;
    }

    // Remove previous caret if any
    if (this.caret && this.caret.parentNode) {
      this.caret.parentNode.removeChild(this.caret);
    }
    this.caret = null;

    // Add caret if enabled
    if (this.caretStyle !== "none") {
      const caret = document.createElement("span");
      caret.className =
        "scramble-caret " +
        (this.caretStyle === "underscore" ? "scramble-caret--underscore" : "scramble-caret--block") +
        (this.caretBlink ? " blink" : "");

      // choose glyph by style unless user overrides with data-scramble-caret
      const glyph = this.caretChar ?? (this.caretStyle === "underscore" ? "_" : "▮");
      caret.textContent = glyph;

      // append caret to last used container (most reliable)
      (state.lastContainer || this.element).appendChild(caret);

      this.caret = caret;
      // Ensure the caret starts in a consistent visual state.
      // We explicitly re-apply the blink class here so that each
      // animation run resets the caret behavior (important when
      // reusing the same instance or mixing different caretEnd modes).
      if (this.caretBlink) this.caret.classList.add("blink");
    }

    document.body.removeChild(temp);
  }

  /**
   * Main method to start the text scramble animation
   * @param {string} newText - The final text to reveal
   * @returns {Promise} - Resolves when animation completes
   */
  setText(newText) {
    this._animated = true;
    this._isAnimating = true;
    const promise = new Promise((resolve) => (this.resolve = resolve));
    this.element.classList.add("is-scrambling");
    this.createSpans(newText);

    if (this.style === "type" && this.caret && this.caretStart !== "none") {
      // Put caret at the very start BEFORE the pre-roll (blink or pause)
      this.moveCaretToIndex(-1);
    }

    cancelAnimationFrame(this.frameRequest);
    this.frame = 0;
    this.hasStarted = false;

    // Handle caret pre-start phase
    if (this.style === "type" && this.caret && this.caretStart !== "none") {
      this.caret.style.opacity = "1";

      if (this.caretStart === "blink") {
        this.caret.classList.add("blink");
      } else {
        this.caret.classList.remove("blink");
      }

      setTimeout(() => {
        this.hasStarted = true;
        this._lastTimestamp = null;
        this.frameRequest = requestAnimationFrame((t) => this.update(t));
      }, this.caretStartDelay);

      return promise;
    }

    // Normal start
    this.hasStarted = true;
    this._lastTimestamp = null;
    this.frameRequest = requestAnimationFrame((t) => this.update(t));
    return promise;
  }

  /**
   * Animation loop - updates on each frame
   * Now updates individual spans instead of rebuilding entire innerHTML
   */
  update(timestamp) {
    if (!this.hasStarted) return;

    if (!this._lastTimestamp) this._lastTimestamp = timestamp;
    const elapsed = timestamp - this._lastTimestamp;
    const framesToAdvance = Math.floor(elapsed / this.speed);

    if (framesToAdvance === 0) {
      this.frameRequest = requestAnimationFrame((t) => this.update(t));
      return;
    }

    this._lastTimestamp += framesToAdvance * this.speed;
    this.frame += framesToAdvance;

    let complete = 0;
    let lastRevealed = -1;

    for (let i = 0, n = this.queue.length; i < n; i++) {
      const { to, start, end } = this.queue[i];
      const span = this.spans[i];
      if (!span) continue;

      if (this.style === "type") {
        if (this.frame >= start) {
          span.textContent = to;
          span.style.opacity = "1";
          complete++;
          lastRevealed = i;
        } else {
          span.style.opacity = "0";
        }
        continue;
      }

      // default: decrypt mode (your existing logic)
      let { char } = this.queue[i];
      if (this.frame >= end) {
        complete++;
        span.textContent = to;
        span.style.opacity = "1";
        span.classList.remove("active");
      } else if (this.frame >= start) {
        if (!char || Math.random() < 0.5) {
          char = this.randomChar();
          this.queue[i].char = char;
        }
        span.textContent = char;
        span.style.opacity = "1";
        span.style.textAlign = "center";
        span.classList.add("active");
      } else {
        span.style.opacity = "0";
      }
    }

    // caret positioning/behavior
    if (this.style === "type" && this.caret) {
      // keep caret following the revealed text
      this.moveCaretToIndex(lastRevealed);

      if (complete === this.queue.length) {
        // finished typing
        if (this.caretEnd === "hide") {
          this.caret.style.opacity = "0";
        } else {
          this.caret.style.opacity = "1";

          if (this.caretEnd === "stay-solid") {
            this.caret.classList.remove("blink");
          }
        }
      } else {
        // still typing
        this.caret.style.opacity = "1";

        // ensure blink behavior matches settings while animating
        if (this.caretBlink) this.caret.classList.add("blink");
        else this.caret.classList.remove("blink");
      }
    } else if (this.caret) {
      // for non-typing mode (if you ever enable caret there)
      this.caret.style.opacity = complete === this.queue.length ? "0" : "1";
    }

    if (complete === this.queue.length) {
      this._isAnimating = false;
      this.finalize();
      this.resolve?.();
    } else {
      this.frameRequest = requestAnimationFrame((t) => this.update(t));
    }
  }

  /**
   * Get random character from character set
   * @returns {string} - Random character
   */
  randomChar() {
    return this.chars[Math.floor(Math.random() * this.chars.length)];
  }
}

/**
 * INITIALIZATION
 * ==============
 * Automatically finds all .scramble-text elements and sets up animations
 */
// ---------------------------------------------------------------------------
// Hero logo controller — set once from main.js, called by the "logo-reveal" step
// ---------------------------------------------------------------------------
let _heroLogoController = null;

/**
 * Hand over the StrokeRevealCanvas instance so the orchestrator can call .start().
 * Called once from main.js after initFlowfieldParticles() returns.
 */
function setHeroLogoController(controller) {
  _heroLogoController = controller;
}

/**
 * Fired by the "logo-reveal" step in the hero group sequence.
 * Calls .start() on the logo (which is a no-op if already animating or queues
 * via pendingStart if SVG hasn't finished loading yet).
 * Returns immediately — the logo animates via rAF on its own, in parallel with
 * whatever else the orchestrator fires at the same time (e.g. pixel-scramble).
 */
function animateLogoReveal() {
  if (_heroLogoController?.turnOn) _heroLogoController.turnOn();
  else if (_heroLogoController?.start) _heroLogoController.start();
  return Promise.resolve();
}

function initTextScramble() {
  // Collect BOTH text steps and generic steps, in DOM order
  const stepElements = Array.from(document.querySelectorAll(".scramble-text, .scramble-step"));
  if (stepElements.length === 0) return;

  const allTextInstances = [];

  // Group steps by nearest [data-scramble-group] container
  // (fallback: nearest <section>, then parent node)
  const groups = new Map(); // key: groupRoot element, value: array of steps

  for (const el of stepElements) {
    const groupRoot = el.closest("[data-scramble-group]") || el.closest("section") || el.parentElement;

    if (!groupRoot) continue;
    if (!groups.has(groupRoot)) groups.set(groupRoot, []);

    // TEXT step
    if (el.classList.contains("scramble-text")) {
      const instance = new TextScramble(el);
      allTextInstances.push(instance);

      groups.get(groupRoot).push({
        el,
        type: "text",
        run: () => instance.setText(instance.originalText),
        _instance: instance,
      });

      continue;
    }

    // GENERIC step (placeholder for future effects)
    if (el.classList.contains("scramble-step")) {
      const stepType = el.getAttribute("data-step") || "noop";

      groups.get(groupRoot).push({
        el,
        type: stepType,
        run: () => runGenericStep(el, stepType),
      });
    }
  }

  // Run animation for a single group
  const runGroupSequence = (steps, groupRoot) => {
    // Group consecutive parallel steps together
    const sequences = [];
    let currentSequence = [];
    let isParallel = false;

    steps.forEach((step) => {
      const stepIsParallel = step.el.classList?.contains("scramble-parallel");

      if (stepIsParallel !== isParallel && currentSequence.length > 0) {
        sequences.push({ steps: currentSequence, parallel: isParallel });
        currentSequence = [];
      }

      currentSequence.push(step);
      isParallel = stepIsParallel;
    });

    if (currentSequence.length > 0) {
      sequences.push({ steps: currentSequence, parallel: isParallel });
    }

    // Execute sequences
    const runNextSequence = (index = 0) => {
      if (index >= sequences.length) return;

      const { steps: seqSteps, parallel } = sequences[index];

      if (parallel) {
        const promises = [];

        seqSteps.forEach((step) => {
          if (step.el._animated) return;
          step.el._animated = true;
          promises.push(Promise.resolve(step.run()));
        });

        Promise.all(promises).then(() => {
          setTimeout(() => runNextSequence(index + 1), 200);
        });

        return;
      } else {
        // Run sequentially
        const runNext = (stepIndex = 0) => {
          if (stepIndex >= seqSteps.length) {
            runNextSequence(index + 1);
            return;
          }

          const step = seqSteps[stepIndex];
          if (step.el._animated) return runNext(stepIndex + 1);
          step.el._animated = true;

          Promise.resolve(step.run()).then(() => {
            setTimeout(() => runNext(stepIndex + 1), 200);
          });
        };
        runNext(0);
      }
    };

    runNextSequence(0);
  };

  // Observe only TOP-LEVEL groups (not nested ones)
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const groupRoot = entry.target;
        const steps = groups.get(groupRoot);
        if (!steps) continue;

        runGroupSequence(steps, groupRoot);
        observer.unobserve(groupRoot);
      }
    },
    { threshold: 0.3 },
  );

  // Only observe groups that are NOT nested inside another group
  for (const groupRoot of groups.keys()) {
    const hasParentGroup = groupRoot.parentElement?.closest("[data-scramble-group]");
    if (!hasParentGroup) {
      observer.observe(groupRoot);
    }
  }
  // Unlock only when viewport changes (resize / orientation change)
  let resizeTimer = null;

  const handleViewportChange = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      allTextInstances.forEach((inst) => {
        if (inst._animated && !inst._isAnimating) inst.unlock();
      });
    }, 150);
  };

  window.addEventListener("resize", handleViewportChange, { passive: true });
  window.addEventListener("orientationchange", handleViewportChange, { passive: true });
}

/**
 * Placeholder for future animations.
 * For now, generic steps do nothing but allow sequencing.
 * Later you can switch by stepType and return a Promise that resolves on animation end.
 */
function runGenericStep(el, stepType) {
  // Example future structure:
  // if (stepType === "scanline") return animateScanline(el);
  // if (stepType === "fade") return animateFade(el);

  if (!el) return Promise.resolve();

  if (stepType === "logo-reveal") return animateLogoReveal();
  if (stepType === "pixel-scramble") return animatePixelScramble(el);

  return Promise.resolve();
}

function animatePixelScramble(stepEl) {
  const targets = Array.from(stepEl.querySelectorAll(".btn"));
  if (targets.length === 0) return Promise.resolve();

  // Check animation mode
  const animMode = stepEl.getAttribute("data-pixel-anim-mode") || "parallel";

  if (animMode === "sequential") {
    // Animate one after another
    return targets.reduce((promise, btn) => {
      return promise.then(() => {
        return pixelScrambleElement(btn, stepEl).then(() => {
          // Optional delay between buttons
          const delay = parseInt(stepEl.getAttribute("data-pixel-anim-delay")) || 0;
          return new Promise((resolve) => setTimeout(resolve, delay));
        });
      });
    }, Promise.resolve());
  }

  // Default: parallel (all at once)
  return Promise.all(targets.map((btn) => pixelScrambleElement(btn, stepEl)));
}

/**
 * CANVAS-BASED PIXEL SCRAMBLE EFFECT
 * ===================================
 *
 * Modular digital distortion effects that reveal buttons with style.
 *
 * MODES:
 * ------
 * data-pixel-mode="default"            - Glitch noise wipe (left to right)
 * data-pixel-mode="scanline"           - Adds bright scanline with intense glitch
 * data-pixel-mode="particles"          - Pixels fly in from outside bounds
 * data-pixel-mode="scanline-particles" - Combo: scanline + particles (recommended)
 *
 * BASIC CONFIG:
 * -------------
 * data-pixel-cols="18"                 - Number of columns (default: 14)
 * data-pixel-rows="6"                  - Number of rows (default: 5)
 * data-pixel-duration="1000"           - Total animation time in ms (default: 1000)
 *
 * SCANLINE OPTIONS:
 * -----------------
 * data-pixel-scanline-width="3"        - Scanline thickness in cells (default: 3)
 * data-pixel-scanline-intensity="0.8"  - Brightness boost (default: 0.8)
 * data-pixel-scanline-glow="true"      - Add glow effect (default: true)
 *
 * PARTICLE OPTIONS:
 * -----------------
 * data-pixel-particle-percent="30"     - % of pixels flying in (default: 30)
 * data-pixel-particle-distance="150"   - Starting distance outside (default: 150px)
 * data-pixel-particle-speed="0.8"      - Speed multiplier (default: 0.8)
 *
 * USAGE EXAMPLE:
 * --------------
 * <div class="scramble-step" data-step="pixel-scramble"
 *      data-pixel-mode="scanline-particles"
 *      data-pixel-cols="20"
 *      data-pixel-rows="7"
 *      data-pixel-scanline-width="4">
 *   <a href="#" class="btn">Click Me</a>
 * </div>
 */

function pixelScrambleElement(targetEl, stepEl) {
  return new Promise((resolve) => {
    // ---------- Configuration Parser ----------
    const getNum = (attr, fallback) => {
      const v = parseFloat(stepEl?.getAttribute(attr));
      return Number.isFinite(v) ? v : fallback;
    };

    const getBool = (attr, fallback) => {
      const v = stepEl?.getAttribute(attr);
      return v === null ? fallback : v !== "false";
    };

    // Core settings
    const mode = stepEl?.getAttribute("data-pixel-mode") || "default";
    const cols = getNum("data-pixel-cols", 14);
    const rows = getNum("data-pixel-rows", 5);
    const totalDuration = getNum("data-pixel-duration", 1000);

    // Scanline settings
    const scanlineWidth = getNum("data-pixel-scanline-width", 3);
    const scanlineIntensity = getNum("data-pixel-scanline-intensity", 0.8);
    const scanlineGlow = getBool("data-pixel-scanline-glow", true);

    // Particle settings
    const particlePercent = getNum("data-pixel-particle-percent", 30);
    const particleDistance = getNum("data-pixel-particle-distance", 150);
    const particleSpeed = getNum("data-pixel-particle-speed", 0.8);

    // Mode flags
    const hasScanline = mode.includes("scanline");
    const hasParticles = mode.includes("particles");

    // ---------- Setup ----------
    targetEl.classList.add("is-scramble-paint-hidden");
    targetEl.classList.add("is-scramble-locked");

    const cs = window.getComputedStyle(targetEl);

    // Save INLINE styles so we can restore cleanly
    const originalPosition = targetEl.style.position;
    const originalOverflow = targetEl.style.overflow;

    // If it was static, we need relative for the canvas to anchor
    if (cs.position === "static") targetEl.style.position = "relative";

    // IMPORTANT: Allow overflow when particles are enabled so they can start outside button
    const forceOverflow = targetEl.dataset.scrambleOverflow;
    targetEl.style.overflow = forceOverflow || (hasParticles ? "visible" : "hidden");

    // ---------- Color Utilities ----------
    const parseRGB = (str) => {
      const m = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      return m ? [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])] : [128, 128, 128];
    };

    const clamp = (n) => Math.max(0, Math.min(255, Math.floor(n)));

    const tint = (rgb, amt) => {
      const t = amt >= 0 ? 255 : 0;
      const p = Math.abs(amt);
      return [clamp((t - rgb[0]) * p + rgb[0]), clamp((t - rgb[1]) * p + rgb[1]), clamp((t - rgb[2]) * p + rgb[2])];
    };

    const brighten = (rgb, amt) => {
      return [clamp(rgb[0] + amt), clamp(rgb[1] + amt), clamp(rgb[2] + amt)];
    };

    const bgRGB = parseRGB(cs.backgroundColor);
    const fgRGB = parseRGB(cs.color);
    const borderRGB = parseRGB(cs.borderColor);

    const bgDark = tint(bgRGB, -0.35);
    const bgLight = tint(bgRGB, 0.35);
    const colorPalette = [borderRGB, bgRGB, bgDark, bgLight, fgRGB];

    // Scanline color (very bright)
    const scanlineColor = brighten(bgLight, 120);

    // ---------- Canvas Setup ----------
    const canvas = document.createElement("canvas");
    canvas.className = "pixel-scramble-overlay";
    canvas.style.position = "absolute";
    // canvas.style.top = "0";
    // canvas.style.left = "0";
    // canvas.style.width = "100%";
    // canvas.style.height = "100%";
    // Hero buttons hardcoded cover
    canvas.style.top = "-4px";
    canvas.style.left = "-4px";
    canvas.style.width = "calc(100% + 8px)";
    canvas.style.height = "calc(100% + 8px)";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "999";
    canvas.style.borderRadius = cs.borderRadius;

    targetEl.appendChild(canvas);

    targetEl.classList.remove("is-scramble-paint-hidden");

    const rect = targetEl.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;

    const cellW = w / cols;
    const cellH = h / rows;

    // ---------- Pixel Grid Generation ----------
    const getRandomColor = () => {
      return colorPalette[Math.floor(Math.random() * colorPalette.length)];
    };

    const pixels = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const finalX = c * cellW;
        const finalY = r * cellH;

        // Determine if this pixel is a "particle" (flies in from outside)
        const isParticle = hasParticles && Math.random() * 100 < particlePercent;

        let startX = finalX;
        if (isParticle) {
          // Start outside bounds on the right
          startX = w + particleDistance + Math.random() * 100;
        }

        pixels.push({
          finalX,
          finalY,
          currentX: startX,
          x: finalX,
          y: finalY,
          w: cellW,
          h: cellH,
          col: c,
          row: r,
          color: getRandomColor(),
          isParticle,
          particleDelay: isParticle ? Math.random() * 0.3 : 0, // Stagger particle entry
        });
      }
    }

    // ---------- Animation Loop ----------
    const startTime = Date.now();
    let lastColorChange = Date.now();
    const colorChangeInterval = 50;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const globalProgress = Math.min(elapsed / totalDuration, 1);

      // Randomize colors for glitch effect
      if (Date.now() - lastColorChange > colorChangeInterval) {
        pixels.forEach((pixel) => {
          const pixelCenterX = pixel.currentX + pixel.w / 2;
          const sweepX = globalProgress * w;

          // Only randomize if not yet revealed
          if (pixelCenterX > sweepX) {
            pixel.color = getRandomColor();
          }
        });
        lastColorChange = Date.now();
      }

      // Clear canvas
      ctx.clearRect(0, 0, w, h);

      // Calculate sweep position
      const sweepX = globalProgress * w;
      const scanlineX = sweepX;

      // Draw particles (flying in from right)
      // TODO: Particle effect needs canvas expansion to work properly - revisit later
      /*
        if (hasParticles) {
          pixels.forEach((pixel) => {
            if (!pixel.isParticle) return;

            const particleStart = pixel.particleDelay;
            const particleProgress = Math.max(
              0,
              Math.min((globalProgress - particleStart) / (particleSpeed * (1 - particleStart)), 1),
            );

            const eased = particleProgress * particleProgress * (3 - 2 * particleProgress);
            pixel.currentX = pixel.finalX + (pixel.currentX - pixel.finalX) * (1 - eased);

            if (particleProgress < 0.99) {
              ctx.fillStyle = `rgba(${pixel.color[0]}, ${pixel.color[1]}, ${pixel.color[2]}, ${0.7 + particleProgress * 0.3})`;
              ctx.fillRect(pixel.currentX, pixel.y, pixel.w + 1, pixel.h + 1);
            }
          });
        }
        */

      // Draw main glitch pixels (being swept away)
      pixels.forEach((pixel) => {
        if (pixel.isParticle) return; // Skip particles in this pass

        const pixelCenterX = pixel.x + pixel.w / 2;
        const distanceFromSweep = sweepX - pixelCenterX;
        const trailWidth = cellW * 2;

        // Scanline detection
        const inScanline = hasScanline && Math.abs(pixelCenterX - scanlineX) < scanlineWidth * cellW;

        if (pixelCenterX > sweepX) {
          // Not swept yet - draw with noise
          let color = pixel.color;
          let opacity = 0.95;

          // Scanline effect: brighten and intensify glitch
          if (inScanline) {
            color = scanlineColor;
            opacity = 1;
            // Extra randomization at scanline
            if (Math.random() > 0.7) {
              color = getRandomColor();
            }
          }

          ctx.fillStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opacity})`;
          ctx.fillRect(pixel.x, pixel.y, pixel.w + 1, pixel.h + 1);
        } else if (distanceFromSweep < trailWidth) {
          // In trail zone - fade out
          const trailProgress = distanceFromSweep / trailWidth;
          const opacity = 0.95 * (1 - trailProgress);

          if (Math.random() > trailProgress) {
            ctx.fillStyle = `rgba(${pixel.color[0]}, ${pixel.color[1]}, ${pixel.color[2]}, ${opacity})`;
            ctx.fillRect(pixel.x, pixel.y, pixel.w + 1, pixel.h + 1);
          }
        }
      });

      // Scanline glow effect
      if (hasScanline && scanlineGlow && globalProgress < 0.95) {
        const glowGradient = ctx.createLinearGradient(
          scanlineX - scanlineWidth * cellW * 2,
          0,
          scanlineX + scanlineWidth * cellW,
          0,
        );
        glowGradient.addColorStop(0, `rgba(${scanlineColor[0]}, ${scanlineColor[1]}, ${scanlineColor[2]}, 0)`);
        glowGradient.addColorStop(0.5, `rgba(${scanlineColor[0]}, ${scanlineColor[1]}, ${scanlineColor[2]}, 0.3)`);
        glowGradient.addColorStop(1, `rgba(${scanlineColor[0]}, ${scanlineColor[1]}, ${scanlineColor[2]}, 0)`);

        ctx.fillStyle = glowGradient;
        ctx.fillRect(scanlineX - scanlineWidth * cellW * 2, 0, scanlineWidth * cellW * 3, h);
      }

      // Continue or finish
      if (globalProgress < 1) {
        requestAnimationFrame(animate);
      } else {
        canvas.remove();
        // restore inline styles
        targetEl.style.overflow = originalOverflow;
        targetEl.style.position = originalPosition;

        targetEl.classList.remove("is-scramble-locked");
        resolve();
      }
    };

    animate();
  });
}

/**
 * AUTO-INITIALIZE
 * ===============
 * Automatically runs when DOM is ready
 * Remove this if you want manual control over initialization
 */
// if (document.readyState === "loading") {
//   document.addEventListener("DOMContentLoaded", initTextScramble);
// } else {
//   initTextScramble();
// }

/**
 * EXPORT
 * ======
 * Export for manual initialization if needed
 */
export { TextScramble, initTextScramble, setHeroLogoController, pixelScrambleElement };
